import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Tooltip, Snackbar, Alert } from "@mui/material";
import { ProductCard, RecommendedType, UOM } from "../../features/createSecondaryOrder/CreateSecondaryOrderType";
import { getMOQColors, getStockCountColors } from "./util";
import DummyProductImage from "../../assets/image/png/dummyProduct.png";
import { OrderType } from "../../types/types";
import { performUomConversion } from "../../services/catalogueService";
import { updateMultiQtyInCart } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import "./CatalogueProductCard.css";
import { truncateToTwoDecimals } from "../dms_common/DmsUtils";
import { ReactComponent as PromoIcon } from "../../assets/image/svg/promoIcon.svg";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RenderSvg from "../renderSvg/RenderSvg";
const CatalogueProductCard: React.FC<ProductCard> = (props) => {
  const {
    productUI,
    updateQuantityInCart,
    orderType,
    handleClickViewSkuDetails,
    catalogue,
    setCart,
    customerInfo,
    addSuggestedQtyState,
    cartProductMap,
    oneClickOrderConfig,
    disableStockCheck,
    enableMOQCheck,
    showListView
  } = props;

  const {
    batchCode,
    uom,
    caseToPieceQty,
    otherUnitToPieceQuantity,
    availableCaseQty,
    availableOtherQty,
    availablePieceQty,
    otherUnitName,
    isRecommended,
    price, // piece ptr
    mrp, // piece mrp
    
    casePtr, //case ptr
    caseMrp, //caseMrp
    minimumOrderQuantity = 0,
    pieceSize
  } = productUI || {};
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [caseQuantityInput, setCaseQuantityInput] = useState<string>(
    productUI?.caseQuantity?.toString() || ""
  );
  const [pieceQuantityInput, setPieceQuantityInput] = useState<string>(
    productUI?.pieceQuantity?.toString() || ""
  );
  const [otherUnitQuantityInput, setOtherUnitQuantityInput] = useState<string>(
    productUI?.otherUnitQuantity?.toString() || ""
  );

  const [isEditingCaseQuantity, setIsEditingCaseQuantity] = useState<boolean>(false);
  const [isEditingPieceQuantity, setIsEditingPieceQuantity] = useState<boolean>(false);
  const [isEditingOtherUnitQuantity, setIsEditingOtherUnitQuantity] = useState<boolean>(false);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    ptrLabel,
    ptdLabel,
    mrpLabel,
    unitLabel,
    caseUnitLabel,
  otherUnitLabel,
    packUnitLabel,
    currency,
    uomConversion,
    designationBasedUomSecondary,
    disableOtherUnitNameWiseCavinKair,
    displayOtherUnitName,
    hideMrpLabel = false
  } = ConfigDictionary;

  let customOtherUnitLabel = displayOtherUnitName
    ? (otherUnitName && otherUnitName.length > 0 ? otherUnitName : otherUnitLabel)
    : otherUnitLabel;

  const recommendationType: RecommendedType = productUI.recommendationPriority === 0 ? "regular" : (productUI.recommendationPriority === 1 ? "must-try" : (productUI.recommendationPriority === 2) ? "recovery" : undefined);

  const handleMultiQtyChange = (newCaseQty: number, newOtherQty: number, newPieceQty: number) => {
    const { convertedCaseQty, convertedOtherQty, convertedPieceQty, hasQtyExceededStock } =
      performUomConversion(
        newCaseQty,
        newOtherQty,
        newPieceQty,
        uom,
        caseToPieceQty,
        otherUnitToPieceQuantity,
        availableCaseQty,
        availableOtherQty,
        availablePieceQty,
        undefined,
        otherUnitName
      );
    if (hasQtyExceededStock) setSnackbarOpen(true);
    setCaseQuantityInput(convertedCaseQty.toString());
    setOtherUnitQuantityInput(convertedOtherQty.toString());
    setPieceQuantityInput(convertedPieceQty.toString());
    updateMultiQtyInCart(
      batchCode,
      {
        caseQuantity: convertedCaseQty,
        otherUnitQuantity: convertedOtherQty,
        pieceQuantity: convertedPieceQty,
      },
      customerInfo,
      catalogue,
      setCart
    );
  };

  useEffect(() => {
    if((oneClickOrderConfig?.isAllowed && oneClickOrderConfig?.isAllowed) && addSuggestedQtyState === 1 && isRecommended && ((productUI.suggestedCaseQuantity + productUI.suggestedQuantity + productUI.suggestedOtherQuantity) > 0)) {
      if(Number(caseQuantityInput) < productUI.suggestedCaseQuantity) {
        setCaseQuantityInput(productUI.suggestedCaseQuantity.toString());
      }
      if(Number(pieceQuantityInput) < productUI.suggestedQuantity) {
        setPieceQuantityInput(productUI.suggestedQuantity.toString());
      }
      if(Number(otherUnitQuantityInput) < productUI.suggestedOtherQuantity) {
        setOtherUnitQuantityInput(productUI.suggestedOtherQuantity.toString());
      }
    }
  }, [addSuggestedQtyState])

  useEffect(() => {
    if((oneClickOrderConfig?.isAllowed && oneClickOrderConfig?.isAllowed) && isRecommended && cartProductMap) {
      let productMap = cartProductMap?.[batchCode];
      setCaseQuantityInput((productMap?.caseQuantity ?? 0).toString());
      setPieceQuantityInput((productMap?.pieceQuantity ?? 0).toString());
      setOtherUnitQuantityInput((productMap?.otherUnitQuantity ?? 0).toString());
    }
  },[cartProductMap])

  const handleIncOrDecQty = (type: UOM, change: number) => {
    if (uomConversion) {
      const newCaseQty = type === UOM.CASE ? +caseQuantityInput + change : +caseQuantityInput;
      const newOtherQty =
        type === UOM.OTHER ? +otherUnitQuantityInput + change : +otherUnitQuantityInput;
      const newPieceQty = type === UOM.PIECE ? +pieceQuantityInput + change : +pieceQuantityInput;
      handleMultiQtyChange(newCaseQty, newOtherQty, newPieceQty);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.max(+caseQuantityInput + change, 0).toString()
          : type === UOM.OTHER
          ? Math.max(+otherUnitQuantityInput + change, 0).toString()
          : Math.max(+pieceQuantityInput + change, 0).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const handleInputBlur = (type: UOM) => {
    if (uomConversion) {
      handleMultiQtyChange(+caseQuantityInput, +otherUnitQuantityInput, +pieceQuantityInput);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.min(+caseQuantityInput, availableCaseQty).toString()
          : type === UOM.OTHER
          ? Math.min(+otherUnitQuantityInput, availableOtherQty).toString()
          : Math.min(+pieceQuantityInput, availablePieceQty).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const isInStock = Boolean(availableCaseQty || availablePieceQty || availableOtherQty);
  const availableUoms = uom?.replace(/\s+/g, "").split(",");
  const disablePieceUom = (!availableUoms.includes("all") && !availableUoms.includes("ea"));
  const disableCaseUom = (!availableUoms.includes("all") && !availableUoms.includes("cs")) || (uomConversion && (caseToPieceQty === 0));
  const disableOtherUom = (!availableUoms.includes("all") && !availableUoms.includes("other")) || (uomConversion && (otherUnitToPieceQuantity === 0)) || (disableOtherUnitNameWiseCavinKair && otherUnitName?.trim().length === 0);

  const { bg, text, border } = getStockCountColors(isInStock);
  const { bgMOQ, textMOQ } = getMOQColors()
  const checkIfNormalizedQtyMatchesOrExceedsStock = () => {
    const normalizedQty =
      +caseQuantityInput * caseToPieceQty +
      +otherUnitQuantityInput * otherUnitToPieceQuantity +
      +pieceQuantityInput;
    const normalizedStock =
      availableCaseQty * caseToPieceQty +
      availableOtherQty * otherUnitToPieceQuantity +
      availablePieceQty;
    return normalizedQty >= normalizedStock;
  };

  const isValidQuantity = (type: UOM) => {
    if (uomConversion) {
      return !checkIfNormalizedQtyMatchesOrExceedsStock();
    }
    if(disableStockCheck) return true;
    if (type === UOM.PIECE) {
      return availablePieceQty > +pieceQuantityInput;
    } else if (type === UOM.CASE) {
      return availableCaseQty > +caseQuantityInput;
    } else {
      return availableOtherQty > +otherUnitQuantityInput;
    }
  };

 const checkIfNormalizedQtyMatchesOrBelowMOQ = () => {
    const normalizedQty =
      +caseQuantityInput * caseToPieceQty +
      +otherUnitQuantityInput * otherUnitToPieceQuantity +
      +pieceQuantityInput;
    return normalizedQty <= minimumOrderQuantity
  } 
  const isQuantityAboveOrEqualMOQ = () => {
    if (!enableMOQCheck) return true;
    return !checkIfNormalizedQtyMatchesOrBelowMOQ()
  }
  
  const currentTotalQuantity = () => {
    const normalizedQty =
      +caseQuantityInput * caseToPieceQty +
      +otherUnitQuantityInput * otherUnitToPieceQuantity +
      +pieceQuantityInput;
    return normalizedQty
  }
  const makeQtyEqualsMOQ = () => {
    const { convertedCaseQty, convertedOtherQty, convertedPieceQty } = performUomConversion(0,0,minimumOrderQuantity,uom,caseToPieceQty,otherUnitToPieceQuantity,0,0,0,true, otherUnitName);
    handleMultiQtyChange(convertedCaseQty, convertedOtherQty, convertedPieceQty)
  }

  const makeQtyEqualZero = () => {
    handleMultiQtyChange(0, 0, 0)
  }

  const handleAddClick = (uom: UOM, quantity: number) => {
    if (disableAddButton(uom)) return;
      if ( enableMOQCheck && currentTotalQuantity() + quantity < minimumOrderQuantity ) {
        makeQtyEqualsMOQ();
      } else {
        handleIncOrDecQty(uom, 1);
    }
  };

  const handleSubtractClick = (uom: UOM, quantity: number) => {
    if (
      enableMOQCheck &&
      currentTotalQuantity() - quantity < minimumOrderQuantity
    ) {
      makeQtyEqualZero();
    } else {
      handleIncOrDecQty(uom, -1);
    }
  };

  const disableAddButton = (type: UOM) => {
    if (
      (type === UOM.CASE && disableCaseUom) ||
      (type === UOM.OTHER && disableOtherUom) ||
      (type === UOM.PIECE && disablePieceUom)
    ) {
      return true;
    }
    if (enableMOQCheck && !disableStockCheck) {
      const normalizedStock =
        availableCaseQty * caseToPieceQty +
        availableOtherQty * otherUnitToPieceQuantity +
        availablePieceQty;

      if (normalizedStock === 0 && minimumOrderQuantity === 0) {
        return true;
      }

      if (normalizedStock < minimumOrderQuantity) {
        return true;
      }

      if (uomConversion) {
        return checkIfNormalizedQtyMatchesOrExceedsStock();
      }
    }
    if (uomConversion) {
      return checkIfNormalizedQtyMatchesOrExceedsStock();
    }
    return ( !(disableStockCheck && disableStockCheck) && (
      (type === UOM.CASE && !availableCaseQty) ||
      (type === UOM.OTHER && !availableOtherQty) ||
      (type === UOM.PIECE && !availablePieceQty)
    ));
  };

  const getDisabledButtonStyling = () => {
    return {
      cursor: "not-allowed",
      backgroundColor: "#F4F4F4",
      color: "#7C7C7C",
    };
  };

  const getOneClickImgBackgroundStyling = () => {
    return recommendationType === "must-try" ? oneClickOrderConfig?.mustTry.imageBackground : ( recommendationType === "recovery" ? oneClickOrderConfig?.recovery.imageBackground : "#fafafa");
  };

  const getOneClickLabelBackgroundStyling = () => {
    return recommendationType === "must-try" ?  oneClickOrderConfig?.mustTry.lableBackground : (recommendationType === "recovery" ? oneClickOrderConfig?.recovery.lableBackground : "#fafafa");
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getSuggestedQty = (): string => {
    const qtyParts = [];
    
    if (productUI.suggestedCaseQuantity > 0) qtyParts.push(`${productUI.suggestedCaseQuantity} ${productUI.suggestedOtherQuantity || productUI.suggestedQuantity ? caseUnitLabel : 'Case'}`);
    if (productUI.suggestedOtherQuantity > 0) qtyParts.push(`${productUI.suggestedOtherQuantity} ${productUI.suggestedCaseQuantity || productUI.suggestedQuantity ? otherUnitLabel : 'Other'}`);
    if (productUI.suggestedQuantity > 0) qtyParts.push(`${productUI.suggestedQuantity} ${productUI.suggestedCaseQuantity || productUI.suggestedOtherQuantity ? packUnitLabel : 'Piece'}`);
   
    return qtyParts.join(", ");
  };

  const gridItemSize = orderType === OrderType.PRIMARY ? 12 : otherUnitLabel ? 4 : packUnitLabel ? 6 : 12;
 
  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];

  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);

    let visibleUomCount = 0;
  if (designationEnabled && designation && designationKeys && matchedRole) {
      if (matchedRole.uomAllowed.case?.trim() && caseUnitLabel) {
          visibleUomCount++;
      }
      if (matchedRole.uomAllowed.other?.trim() && otherUnitLabel) {
          visibleUomCount++;
      }
      if (matchedRole.uomAllowed.piece?.trim() && packUnitLabel) {
          visibleUomCount++;
      }
  } else {
      if (caseUnitLabel) {
          visibleUomCount++;
      }
      if (otherUnitLabel) {
          visibleUomCount++;
      }
      if (packUnitLabel) {
          visibleUomCount++;
      }
  }

  if (showListView) {
    return (
      <div>
        <div className="CatalogueProductCardListView_container">
          <Snackbar
            open={snackbarOpen}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={handleSnackbarClose}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity="warning"
              sx={{ width: "100%" }}
            >
              The entered quantity exceeds the available stock. Your quantity
              will be adjusted to match the available stock for this SKU.
            </Alert>
          </Snackbar>
          <div className="CatalogueProductCardListView_container_left">
            <div className="imagecontainerfhfhf">
              <img
                height={productUI.imageUrl ? "100%" : "92%"}
                width={productUI.imageUrl ? "100%" : "92%"}
                src={
                  productUI.imageUrl ||
                  ConfigDictionary?.defaultCatalogueImage ||
                  DummyProductImage
                }
                alt={productUI.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null; // Prevent infinite loop
                  target.src = DummyProductImage; // Fallback to dummy image
                }}
              />
            </div>
            <div className="skudetailscontainersdadfd">
              <Tooltip title={`${productUI.name}, ${productUI.pieceSize}`} placement="top">
                <p className="skudetailscontainertop">{`${productUI.name}, ${productUI.pieceSize}`}</p>
              </Tooltip>

              <div className="skudetailscontainerbottom">
                {designationEnabled && designation && designationKeys && matchedRole ? (
                  <>
                    {matchedRole.uomAllowed.case?.trim() && (
                      <>
                        <p className="bottomptd">
                          {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency} {truncateToTwoDecimals(productUI.casePtr ?? 0)}
                        </p>
                        {
                          !hideMrpLabel && 
                          <p className="bottommrp">
                            {mrpLabel || "MRP"}* {currency} {truncateToTwoDecimals(productUI.caseMrp ?? 0)}
                          </p>
                        }
                      </>
                    )}
                    {matchedRole.uomAllowed.piece?.trim() && (
                      <>
                        <p className="bottomptd">
                          {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency} {truncateToTwoDecimals(productUI.price)}
                        </p>
                        {
                          !hideMrpLabel && 
                          <p className="bottommrp">
                            {mrpLabel || "MRP"}* {currency} {truncateToTwoDecimals(productUI.mrp)}
                          </p>
                        }
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="bottomptd">
                      {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency} {truncateToTwoDecimals(productUI.price ?? 0)}
                    </p>
                    {
                      !hideMrpLabel &&
                      <p className="bottommrp">
                        {mrpLabel || "MRP"}* {currency} {truncateToTwoDecimals(productUI.mrp ?? 0)}
                      </p>
                    }
                  </>
                )}
                <div className="bottomviewskudetails">
                  {/* <div
                    className="promos_available_secondary"
                    style={{
                      visibility:
                        Boolean(productUI?.schemes?.length)
                          ? `visible`
                          : `hidden`,
                    }}
                  >
                    <PromoIcon style={{marginRight: "3px"}}/>
                    <span>Promos Available</span>
                  </div> */}
                </div>
                <div className="sku_details_label_container_listView">
                  <div
                    className="view_sku_details_listView"
                    onClick={() => handleClickViewSkuDetails && handleClickViewSkuDetails(productUI)}
                    style={!Boolean(productUI?.schemes?.length)  ? {
                      background: "none",
                      color: "rgb(92 91 91)",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                    } : {}}
                  >
                    View Details
                  <KeyboardArrowRightIcon
                    style={{
                      color: "rgb(92 91 91)",
                      width: "14px"
                    }}
                  />
                  {Boolean(productUI?.schemes?.length) &&
                    <RenderSvg name="skuDetailsPromoIcon" />
                  }
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="CatalogueProductCardListView_container_right">
            <div className="quantityinputscontainerdsfcdsf" style={{ width: `${visibleUomCount * 14.5}%` }}>
              <Grid
                container
                spacing={0.5}
                className="product_selection_grid"
                color={"#f4f4f4"}
              >
                {designationEnabled && designation && designationKeys && matchedRole ? (
                  <>
                    {matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{caseUnitLabel?.toUpperCase()}
                          </span>
                        </Box>
                      </Grid>
                    )}
                    {matchedRole.uomAllowed.other?.trim() && otherUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}
                          </span>
                        </Box>
                      </Grid>
                    )}
                    {matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.price ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{packUnitLabel?.toUpperCase()}
                          </span>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {caseUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{caseUnitLabel?.toUpperCase()}
                          </span>
                        </Box>
                      </Grid>
                    )}
                    {otherUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}
                          </span>
                        </Box>
                      </Grid>
                    )}
                    {packUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        <Box className="price_box">
                          <span className="CatalogueProductCard_price_text">
                            {currency}
                            {truncateToTwoDecimals(productUI.price ?? 0)}
                          </span>
                          <span className="CatalogueProductCard_unit_label_text">
                            /{packUnitLabel?.toUpperCase()}
                          </span>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
              <Grid container spacing={0.5} className="product_selection_grid">
                {designationEnabled && designation && designationKeys && matchedRole ? (
                  <>
                    {matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+caseQuantityInput || isEditingCaseQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.CASE, caseToPieceQty)}
                              className="increment_decrement_icon decrement_icon_cs"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={caseQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setCaseQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingCaseQuantity(true)}
                                onBlur={() => {
                                  setIsEditingCaseQuantity(false);
                                  handleInputBlur(UOM.CASE);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_cs"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                              className="increment_decrement_icon increment_icon_cs"
                              style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.CASE, caseToPieceQty)}
                            className="add_and_unit_button add_and_unit_button_cs"
                            style={disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.CASE) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              {caseToPieceQty} {unitLabel?.toUpperCase() || "UNIT"}
                              {caseToPieceQty > 1 ? "S" : ""}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                    {matchedRole.uomAllowed.other?.trim() && otherUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+otherUnitQuantityInput || isEditingOtherUnitQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.OTHER, otherUnitToPieceQuantity)}
                              className="increment_decrement_icon decrement_icon_other"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={otherUnitQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setOtherUnitQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingOtherUnitQuantity(true)}
                                onBlur={() => {
                                  setIsEditingOtherUnitQuantity(false);
                                  handleInputBlur(UOM.OTHER);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_other"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, 1)}
                              className="increment_decrement_icon increment_icon_other"
                              style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.OTHER, otherUnitToPieceQuantity)}
                            className="add_and_unit_button add_and_unit_button_other"
                            style={disableAddButton(UOM.OTHER) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.OTHER) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              {otherUnitToPieceQuantity} {unitLabel?.toUpperCase() || "UNIT"}
                              {otherUnitToPieceQuantity > 1 ? "S" : ""}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                    {matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+pieceQuantityInput || isEditingPieceQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                              className="increment_decrement_icon decrement_icon_pc"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={pieceQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setPieceQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingPieceQuantity(true)}
                                onBlur={() => {
                                  setIsEditingPieceQuantity(false);
                                  handleInputBlur(UOM.PIECE);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_pc"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                              className="increment_decrement_icon increment_icon_pc"
                              style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.PIECE, 1)}
                            className="add_and_unit_button add_and_unit_button_pc"
                            style={disableAddButton(UOM.PIECE) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.PIECE) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              1 {unitLabel?.toUpperCase() || "UNIT"}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {caseUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+caseQuantityInput || isEditingCaseQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.CASE, caseToPieceQty)}
                              className="increment_decrement_icon decrement_icon_cs"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={caseQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setCaseQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingCaseQuantity(true)}
                                onBlur={() => {
                                  setIsEditingCaseQuantity(false);
                                  handleInputBlur(UOM.CASE);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_cs"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                              className="increment_decrement_icon increment_icon_cs"
                              style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.CASE, caseToPieceQty)}
                            className="add_and_unit_button add_and_unit_button_cs"
                            style={disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.CASE) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              {caseToPieceQty} {unitLabel?.toUpperCase() || "UNIT"}
                              {caseToPieceQty > 1 ? "S" : ""}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                    {otherUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+otherUnitQuantityInput || isEditingOtherUnitQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.OTHER, otherUnitToPieceQuantity)}
                              className="increment_decrement_icon decrement_icon_other"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={otherUnitQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setOtherUnitQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingOtherUnitQuantity(true)}
                                onBlur={() => {
                                  setIsEditingOtherUnitQuantity(false);
                                  handleInputBlur(UOM.OTHER);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_other"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, 1)}
                              className="increment_decrement_icon increment_icon_other"
                              style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.OTHER, otherUnitToPieceQuantity)}
                            className="add_and_unit_button add_and_unit_button_other"
                            style={disableAddButton(UOM.OTHER) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.OTHER) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              {otherUnitToPieceQuantity} {unitLabel?.toUpperCase() || "UNIT"}
                              {otherUnitToPieceQuantity > 1 ? "S" : ""}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                    {packUnitLabel && (
                      <Grid item xs={gridItemSize}>
                        {+pieceQuantityInput || isEditingPieceQuantity ? (
                          <Box className="quantity_box">
                            <Box
                              onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                              className="increment_decrement_icon decrement_icon_pc"
                            >
                              -
                            </Box>
                            <Box sx={{ width: "60%", height: "100%" }}>
                              <input
                                inputMode="numeric"
                                value={pieceQuantityInput}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d{0,4}$/.test(value)) {
                                    setPieceQuantityInput(value);
                                  }
                                }}
                                onFocus={() => setIsEditingPieceQuantity(true)}
                                onBlur={() => {
                                  setIsEditingPieceQuantity(false);
                                  handleInputBlur(UOM.PIECE);
                                  if (!isQuantityAboveOrEqualMOQ()) {
                                    makeQtyEqualsMOQ();
                                  }
                                }}
                                className="quantity_input quantity_input_pc"
                              />
                            </Box>
                            <Box
                              onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                              className="increment_decrement_icon increment_icon_pc"
                              style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                            >
                              +
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleAddClick(UOM.PIECE, 1)}
                            className="add_and_unit_button add_and_unit_button_pc"
                            style={disableAddButton(UOM.PIECE) ? getDisabledButtonStyling() : {}}
                          >
                            ADD
                            <span
                              style={{
                                fontSize: "6.5px",
                                fontWeight: 400,
                                color: !disableAddButton(UOM.PIECE) ? "#ffffff" : "#7C7C7C",
                              }}
                            >
                              1 {unitLabel?.toUpperCase() || "UNIT"}
                            </span>
                          </Box>
                        )}
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </div>
           <div style={{ width: "35%", display: "flex", alignItems: "center", justifyContent: "flex-start"}}> 
            <div
              className="stock-nos-container"
              style={{
                backgroundColor: bg,
                margin: 0,
                padding: `4px 6px`,
                borderRadius: "6px",
              }}
            >
              <span style={{ color: text, fontSize: "11px" }}>{`${
                isInStock ? "In" : "Out of"
              } Stock`}</span>
              <div style={{ gap: "4px" }}>
                {designationEnabled && designation && designationKeys && matchedRole ? (
                  <>
                    {matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availableCaseQty}</span>
                          <span>{caseUnitLabel?.toUpperCase()}</span>
                        </p>
                      </div>
                    )}
                    {matchedRole.uomAllowed.other?.trim() && otherUnitLabel && (
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availableOtherQty}</span>
                          <span>{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}</span>
                        </p>
                      </div>
                    )}
                    {matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availablePieceQty}</span>
                          <span>{packUnitLabel?.toUpperCase()}</span>
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {caseUnitLabel && 
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availableCaseQty}</span>
                          <span>{caseUnitLabel?.toUpperCase()}</span>
                        </p>
                      </div>
                    }
                    {otherUnitLabel && (
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availableOtherQty}</span>
                          <span>{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}</span>
                        </p>
                      </div>
                    )}
                    {packUnitLabel && 
                      <div
                        className="stock-qty"
                        style={{
                          borderColor: border,
                          borderRadius: "5px",
                          padding: "2px 6px",
                        }}
                      >
                        <p className="stock-qty-child">
                          <span>{availablePieceQty}</span>
                          <span>{packUnitLabel?.toUpperCase()}</span>
                        </p>
                      </div>
                    }
                  </>
                )}
              </div>
            </div>
            </div>
            {enableMOQCheck && (
              <div className="moq-nos-container" style={{ backgroundColor: bgMOQ }}>
                <span style={{ color: textMOQ }}>
                  {"Min Piece Qty : "}
                  <span style={{ fontWeight: "bold" }}>{minimumOrderQuantity}</span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="listViewBottomBorder">
          <p className="listViewBottomBorder_child"></p>
        </div>
      </div>
    );
  }

  if(designationEnabled && designation && designationKeys && matchedRole) {
    return (
      <div className="CatalogueProductCard">
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: "100%" }}>
            The entered quantity exceeds the available stock. Your quantity will be adjusted to match the available stock for this SKU.
          </Alert>
        </Snackbar>
        <div className="CatalogueProductCard_img_container" style={{ backgroundColor: "#fafafa" }}>
          <img
            height={productUI.imageUrl ? 90 : 70}
            src={productUI.imageUrl || ConfigDictionary?.defaultCatalogueImage || DummyProductImage}
            alt={productUI.name}
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevent infinite loop
              target.src = DummyProductImage; // Fallback to dummy image
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "0px 10px 1px 10px",
          }}
        >
          <div>
            <Tooltip title={productUI.name} arrow>
              <Typography
                sx={{
                  display: "-webkit-box",
                  "-webkit-box-orient": "vertical",
                  "-webkit-line-clamp": "2",
                  overflow: "hidden",
                  fontSize: "12px",
                  fontWeight: 600,
                  textOverflow: "ellipsis",
                  lineClamp: "2",
                  lineHeight: "1rem",
                  minHeight: "2.6em",
                  whiteSpace: "pre-line",
                  color: "#5c5c5c",
                  marginTop: "8px",
                  marginBottom: "6px",
                }}
              >
                {productUI.name}
              </Typography>
            </Tooltip>
            {matchedRole.uomAllowed.case?.trim() && (<><Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
              {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
              {
                // Check the conditions for showing different prices
             
                truncateToTwoDecimals(productUI.casePtr ?? 0)
            }
            </Typography>
            {
              !hideMrpLabel && 
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#7C7C7C",
                  marginBottom: "3px",
                  lineHeight: "1.4",
                }}
              >
                {mrpLabel || "MRP"}* {currency} 
                { truncateToTwoDecimals(productUI.caseMrp ?? 0) }
              </Typography>
            }
            </>)}
            {matchedRole.uomAllowed.piece?.trim() && (<><Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
              {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
              {
                // Check the conditions for showing different prices
             
                truncateToTwoDecimals(productUI.price)
            }
            </Typography>
            {!hideMrpLabel &&
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#7C7C7C",
                  marginBottom: "3px",
                  lineHeight: "1.4",
                }}
              >
                {mrpLabel || "MRP"}* {currency} 
                {
              
                  truncateToTwoDecimals(productUI.mrp)
              }
              </Typography>
            }
            </>)}

            <div className="stock-nos-container" style={{ backgroundColor: bg }}>
              <span style={{ color: text }}>{`${isInStock ? "In" : "Out of"} Stock`}</span>
              <div>
                  { matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
                <div className="stock-qty" style={{ borderColor: border }}>
                  <span>
                    {availableCaseQty} { matchedRole.uomAllowed.case?.toUpperCase()}
                  </span>
                </div>
                  )}
                { matchedRole.uomAllowed.other?.trim() && otherUnitLabel && (
                  <div className="stock-qty" style={{ borderColor: border }}>
                    <span>
                      {availableOtherQty} { matchedRole.uomAllowed.other?.toUpperCase()?.slice(0, 3)}
                    </span>
                  </div>
                )}
                { matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                <div className="stock-qty" style={{ borderColor: border }}>
                  <span>
                    {availablePieceQty} { matchedRole.uomAllowed.piece?.toUpperCase()}
                  </span>
                </div>
                )}
              </div>
            </div>
            {enableMOQCheck && (
              <div className="moq-nos-container" style={{ backgroundColor: bgMOQ }}>
                <span style={{ color: textMOQ }}>
                  {"Min Piece Qty : "}
                  <span style={{ fontWeight: "bold" }}>{minimumOrderQuantity}</span>
                </span>
              </div>
            )}
          </div>
          <div>
            <Grid container spacing={0.5} className="product_selection_grid" color={"#f4f4f4"}>
              { matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
              <Grid item xs>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{ matchedRole.uomAllowed.case?.toUpperCase()}
                  </span>
                </Box>
              </Grid>
              )}
              { matchedRole.uomAllowed.other?.trim() && otherUnitLabel && (
                <Grid item xs>
                  <Box className="price_box">
                    <span className="CatalogueProductCard_price_text">
                      {currency}
                      {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                    </span>
                    <span className="CatalogueProductCard_unit_label_text">
                      /{ matchedRole.uomAllowed.other?.toUpperCase()?.slice(0, 3)}
                    </span>
                  </Box>
                </Grid>
              )}
              { matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                <Grid item xs>
                  <Box className="price_box">
                    <span className="CatalogueProductCard_price_text">
                      {currency}
                      {truncateToTwoDecimals(productUI.price ?? 0)}
                    </span>
                    <span className="CatalogueProductCard_unit_label_text">
                      /{ matchedRole.uomAllowed.piece?.toUpperCase()}
                    </span>
                  </Box>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={0.5} className="product_selection_grid">
              { matchedRole.uomAllowed.case && (
                <Grid item xs>
                {+caseQuantityInput || isEditingCaseQuantity ? (
                  <Box className="quantity_box">
                    <Box
                      onClick={() => handleSubtractClick(UOM.CASE, caseToPieceQty)}
                      className="increment_decrement_icon"
                    >
                      -
                    </Box>
                    <Box sx={{ width: "40%", height: "100%" }}>
                      <input
                        inputMode="numeric"
                        value={caseQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,4}$/.test(value)) {
                            setCaseQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingCaseQuantity(true)}
                        onBlur={() => {
                          setIsEditingCaseQuantity(false);
                          handleInputBlur(UOM.CASE);
                          if (!isQuantityAboveOrEqualMOQ()) {
                            makeQtyEqualsMOQ();
                          }
                        }}
                        className="quantity_input"
                      />
                    </Box>
                    <Box
                      onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => handleAddClick(UOM.CASE, caseToPieceQty)}
                    className="add_and_unit_button"
                    style={disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}}
                  >
                    ADD
                    <span
                      style={{
                        fontSize: "6.5px",
                        fontWeight: 400,
                        color: !disableAddButton(UOM.CASE) ? "#ffffff" : "#7C7C7C",
                      }}
                    >
                      1 {unitLabel?.toUpperCase() || "UNIT"}
                    </span>
                  </Box>
                )}
              </Grid>
              )}
              { matchedRole.uomAllowed.piece && (
                <Grid item xs>
                {+pieceQuantityInput || isEditingPieceQuantity ? (
                  <Box className="quantity_box">
                    <Box
                      onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                      className="increment_decrement_icon"
                    >
                      -
                    </Box>
                    <Box sx={{ width: "40%", height: "100%" }}>
                      <input
                        inputMode="numeric"
                        value={pieceQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,4}$/.test(value)) {
                            setPieceQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingPieceQuantity(true)}
                        onBlur={() => {
                          setIsEditingPieceQuantity(false);
                          handleInputBlur(UOM.PIECE);
                          if (!isQuantityAboveOrEqualMOQ()) {
                            makeQtyEqualsMOQ();
                          }
                        }}
                        className="quantity_input"
                      />
                    </Box>
                    <Box
                      onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => handleAddClick(UOM.PIECE, 1)}
                    className="add_and_unit_button"
                    style={disableAddButton(UOM.PIECE) ? getDisabledButtonStyling() : {}}
                  >
                    ADD
                    <span
                      style={{
                        fontSize: "6.5px",
                        fontWeight: 400,
                        color: !disableAddButton(UOM.PIECE) ? "#ffffff" : "#7C7C7C",
                      }}
                    >
                      1 {unitLabel?.toUpperCase() || "UNIT"}
                    </span>
                  </Box>
                )}
              </Grid>
              )}
            </Grid>
          </div>
          <div className="sku_details_label_container_secondary">
            <div className="promos_available_secondary" style={{ visibility: Boolean(productUI?.schemes?.length) ? `visible` : `hidden` }}>
              <span>Promos Available</span>
            </div>
            <div
              className="view_sku_details_secondary"
              onClick={() => handleClickViewSkuDetails && handleClickViewSkuDetails(productUI)}
            >
              View details {'>'}
            </div>
          </div>
        </div>
      </div>
    );

  }
  return (
    <div className="CatalogueProductCard">
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="warning"
          sx={{ width: "100%" }}
        >
          The entered quantity exceeds the available stock. Your quantity will
          be adjusted to match the available stock for this SKU.
        </Alert>
      </Snackbar>

      <div
        className="CatalogueProductCard_img_container"
        style={{
          backgroundColor:
            oneClickOrderConfig?.isAllowed &&
            oneClickOrderConfig?.isAllowed &&
            isRecommended
              ? getOneClickImgBackgroundStyling()
              : "#fafafa",
        }}
      >
        {oneClickOrderConfig?.isAllowed &&
          oneClickOrderConfig?.isAllowed &&
          recommendationType &&
          isRecommended &&
          recommendationType !== "regular" && (
            <div
              className="CatalogueProductCard_img_RecommendationType"
              style={{ backgroundColor: getOneClickLabelBackgroundStyling() }}
            >
              {recommendationType === "must-try"
                ? oneClickOrderConfig.mustTry.text
                : oneClickOrderConfig.recovery.text}
            </div>
          )}
        <img
          height={productUI.imageUrl ? 90 : 70}
          src={productUI.imageUrl || ConfigDictionary?.defaultCatalogueImage || DummyProductImage}
          alt={productUI.name}
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null; // Prevent infinite loop
            target.src = DummyProductImage; // Fallback to dummy image
          }}
          style={{
            backgroundColor:
              oneClickOrderConfig?.isAllowed &&
              oneClickOrderConfig?.isAllowed &&
              isRecommended
                ? getOneClickImgBackgroundStyling()
                : "#fafafa",
          }}
        />
        {oneClickOrderConfig?.isAllowed &&
          oneClickOrderConfig?.isAllowed &&
          isRecommended &&
          getSuggestedQty().length > 0 && (
            <div className="CatalogueProductCard_img_suggestedQty_container">
              <div className="CatalogueProductCard_img_suggestedQty">
                <span>{getSuggestedQty()}</span>
                <span>Suggested Qty</span>
              </div>
              <div className="square">
                <div className="diagonal_triangle"></div>
              </div>
            </div>
          )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "0px 10px 1px 10px",
        }}
      >
        <div>
          <Tooltip title={productUI.name} arrow>
            <Typography
              sx={{
                display: "-webkit-box",
                "-webkit-box-orient": "vertical",
                "-webkit-line-clamp": "2",
                overflow: "hidden",
                fontSize: "12px",
                fontWeight: 600,
                textOverflow: "ellipsis",
                lineClamp: "2",
                lineHeight: "1rem",
                minHeight: "2.6em",
                whiteSpace: "pre-line",
                color: "#5c5c5c",
                marginTop: "8px",
                marginBottom: "6px",
              }}
            >
              {productUI.name}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
            {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
            {truncateToTwoDecimals(productUI.price ?? 0)}
          </Typography>
          {!hideMrpLabel &&
            <Typography
              sx={{
                fontSize: "10px",
                color: "#7C7C7C",
                marginBottom: "3px",
                lineHeight: "1.4",
              }}
            >
              {mrpLabel || "MRP"}* {currency} {truncateToTwoDecimals(productUI.mrp ?? 0)}
            </Typography>
          }
          <div className="stock-nos-container" style={{ backgroundColor: bg }}>
            <span style={{ color: text }}>{`${
              isInStock ? "In" : "Out of"
            } Stock`}</span>
            <div>
              {caseUnitLabel &&
                <div className="stock-qty" style={{ borderColor: border }}>
                  <span>
                    {availableCaseQty} {caseUnitLabel?.toUpperCase()}
                  </span>
                </div>
              }
              {otherUnitLabel && (
                <div className="stock-qty" style={{ borderColor: border }}>
                  <span>
                    {availableOtherQty}{" "}
                    {customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}
                  </span>
                </div>
              )}
              {
                packUnitLabel && (
                  <div className="stock-qty" style={{ borderColor: border }}>
                    <span>
                      {availablePieceQty} {packUnitLabel?.toUpperCase()}
                    </span>
                  </div>
                )
              }
            </div>
          </div>
          {enableMOQCheck && (
            <div className="moq-nos-container" style={{ backgroundColor: bgMOQ }}>
              <span style={{ color: textMOQ }}>
                {"Min Piece Qty : "}
                <span style={{ fontWeight: "bold" }}>{minimumOrderQuantity}</span>
              </span>
            </div>
          )}
        </div>
        <div>
          <Grid
            container
            spacing={0.5}
            className="product_selection_grid"
            color={"#f4f4f4"}
          >
            {((caseUnitLabel && orderType !== OrderType.PRIMARY) || (orderType === OrderType.PRIMARY)) &&
              <Grid item xs={gridItemSize}>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.casePrice ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{caseUnitLabel?.toUpperCase()}
                  </span>
                </Box>
              </Grid>
            }
            {orderType !== OrderType.PRIMARY && otherUnitLabel && (
              <Grid item xs={gridItemSize}>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.otherUnitPrice ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}
                  </span>
                </Box>
              </Grid>
            )}
            {orderType !== OrderType.PRIMARY && packUnitLabel && (
              <Grid item xs={gridItemSize}>
                <Box className="price_box">
                  <span className="CatalogueProductCard_price_text">
                    {currency}
                    {truncateToTwoDecimals(productUI.price ?? 0)}
                  </span>
                  <span className="CatalogueProductCard_unit_label_text">
                    /{packUnitLabel?.toUpperCase()}
                  </span>
                </Box>
              </Grid>
            )}
          </Grid>
          {((caseUnitLabel && orderType !== OrderType.PRIMARY) || (orderType === OrderType.PRIMARY)) &&
            <Grid container spacing={0.5} className="product_selection_grid">
              <Grid item xs={gridItemSize}>
                {+caseQuantityInput || isEditingCaseQuantity ? (
                  <Box className="quantity_box">
                    <Box
                      onClick={() => handleSubtractClick(UOM.CASE, caseToPieceQty)}
                      className="increment_decrement_icon decrement_icon_cs"
                    >
                      -
                    </Box>
                    <Box sx={{ width: "60%", height: "100%" }}>
                      <input
                        inputMode="numeric"
                        value={caseQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,4}$/.test(value)) {
                            setCaseQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingCaseQuantity(true)}
                        onBlur={() => {
                          setIsEditingCaseQuantity(false);
                          handleInputBlur(UOM.CASE);
                          if (!isQuantityAboveOrEqualMOQ()) {
                            makeQtyEqualsMOQ();
                          }
                        }}
                        className="quantity_input quantity_input_cs"
                      />
                    </Box>
                    <Box
                      onClick={() =>
                        isValidQuantity(UOM.CASE) &&
                        handleIncOrDecQty(UOM.CASE, 1)
                      }
                      className="increment_decrement_icon increment_icon_cs"
                      style={
                        isValidQuantity(UOM.CASE)
                          ? {}
                          : getDisabledButtonStyling()
                      }
                    >
                      +
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => handleAddClick(UOM.CASE, caseToPieceQty)}
                    className="add_and_unit_button add_and_unit_button_cs"
                    style={
                      disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}
                    }
                  >
                    ADD
                    <span
                      style={{
                        fontSize: "6.5px",
                        fontWeight: 400,
                        color: !disableAddButton(UOM.CASE)
                          ? "#ffffff"
                          : "#7C7C7C",
                      }}
                    >
                      {caseToPieceQty} {unitLabel?.toUpperCase() || "UNIT"}
                      {caseToPieceQty > 1 ? "S" : ""}
                    </span>
                  </Box>
                )}
              </Grid>
              {orderType !== OrderType.PRIMARY && otherUnitLabel && (
                <Grid item xs={gridItemSize}>
                  {+otherUnitQuantityInput || isEditingOtherUnitQuantity ? (
                    <Box className="quantity_box">
                      <Box
                        onClick={() => handleSubtractClick(UOM.OTHER, otherUnitToPieceQuantity)}
                        className="increment_decrement_icon decrement_icon_other"
                      >
                        -
                      </Box>
                      <Box sx={{ width: "60%", height: "100%" }}>
                        <input
                          inputMode="numeric"
                          value={otherUnitQuantityInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,4}$/.test(value)) {
                              setOtherUnitQuantityInput(value);
                            }
                          }}
                          onFocus={() => setIsEditingOtherUnitQuantity(true)}
                          onBlur={() => {
                            setIsEditingOtherUnitQuantity(false);
                            handleInputBlur(UOM.OTHER);
                            if (!isQuantityAboveOrEqualMOQ()) {
                              makeQtyEqualsMOQ();
                            }
                          }}
                          className="quantity_input quantity_input_other"
                        />
                      </Box>
                      <Box
                        onClick={() =>
                          isValidQuantity(UOM.OTHER) &&
                          handleIncOrDecQty(UOM.OTHER, 1)
                        }
                        className="increment_decrement_icon increment_icon_other"
                        style={
                          isValidQuantity(UOM.OTHER)
                            ? {}
                            : getDisabledButtonStyling()
                        }
                      >
                        +
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      onClick={() =>handleAddClick(UOM.OTHER, otherUnitToPieceQuantity)}
                      className="add_and_unit_button add_and_unit_button_other"
                      style={
                        disableAddButton(UOM.OTHER)
                          ? getDisabledButtonStyling()
                          : {}
                      }
                    >
                      ADD
                      <span
                        style={{
                          fontSize: "6.5px",
                          fontWeight: 400,
                          color: !disableAddButton(UOM.OTHER)
                            ? "#ffffff"
                            : "#7C7C7C",
                        }}
                      >
                        {otherUnitToPieceQuantity}{" "}
                        {unitLabel?.toUpperCase() || "UNIT"}
                        {otherUnitToPieceQuantity > 1 ? "S" : ""}
                      </span>
                    </Box>
                  )}
                </Grid>
              )}
              {orderType !== OrderType.PRIMARY && packUnitLabel && (
                <Grid item xs={gridItemSize}>
                  {+pieceQuantityInput || isEditingPieceQuantity ? (
                    <Box className="quantity_box">
                      <Box
                        onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                        className="increment_decrement_icon decrement_icon_pc"
                      >
                        -
                      </Box>
                      <Box sx={{ width: "60%", height: "100%" }}>
                        <input
                          inputMode="numeric"
                          value={pieceQuantityInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,4}$/.test(value)) {
                              setPieceQuantityInput(value);
                            }
                          }}
                          onFocus={() => setIsEditingPieceQuantity(true)}
                          onBlur={() => {
                            setIsEditingPieceQuantity(false);
                            handleInputBlur(UOM.PIECE);
                            if (!isQuantityAboveOrEqualMOQ()) {
                              makeQtyEqualsMOQ();
                            }
                          }}
                          className="quantity_input quantity_input_pc"
                        />
                      </Box>
                      <Box
                        onClick={() =>
                          isValidQuantity(UOM.PIECE) &&
                          handleIncOrDecQty(UOM.PIECE, 1)
                        }
                        className="increment_decrement_icon increment_icon_pc"
                        style={
                          isValidQuantity(UOM.PIECE)
                            ? {}
                            : getDisabledButtonStyling()
                        }
                      >
                        +
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => handleAddClick(UOM.PIECE, 1)}
                      className="add_and_unit_button add_and_unit_button_pc"
                      style={
                        disableAddButton(UOM.PIECE)
                          ? getDisabledButtonStyling()
                          : {}
                      }
                    >
                      ADD
                      <span
                        style={{
                          fontSize: "6.5px",
                          fontWeight: 400,
                          color: !disableAddButton(UOM.PIECE)
                            ? "#ffffff"
                            : "#7C7C7C",
                        }}
                      >
                        1 {unitLabel?.toUpperCase() || "UNIT"}
                      </span>
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
          }
        </div>
        <div className="sku_details_label_container_secondary">
          <div className="promos_available_secondary" style={{ visibility: Boolean(productUI?.schemes?.length) ? `visible` : `hidden` }}>
            <span>Promos Available</span>
          </div>
          <div
            className="view_sku_details_secondary"
            onClick={() =>
              handleClickViewSkuDetails && handleClickViewSkuDetails(productUI)
            }
          >
            View details {'>'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogueProductCard;
